import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Leaf,
  ShieldCheck,
  Sparkles,
  CreditCard,
  Recycle,
  ShieldPlus,
  ArrowUpRight,
  ArrowDown,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import Marquee from "@/components/site/Marquee";
import QuoteButton from "@/components/site/QuoteButton";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { CONTACT, IMAGES, MANIFESTO } from "@/lib/site";

const BENEFITS = [
  { Icon: Leaf, label: "A+ Energy Rated" },
  { Icon: ShieldCheck, label: "Enhanced Security" },
  { Icon: Sparkles, label: "Low Maintenance" },
  { Icon: CreditCard, label: "Finance Available" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <Seo
        title="Eminence Windows & Doors | Premium Windows & Doors, Greater Manchester"
        description="Premium uPVC & composite windows and doors, expertly installed across Greater Manchester. Save up to £200 per window and £300 per door. 10-year insurance-backed guarantee. Request a free quote."
      />

      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        className="relative flex min-h-screen items-end overflow-hidden"
        data-testid="hero"
      >
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={IMAGES.heroHome}
            alt="Elegant modern home with premium windows and doors"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 to-transparent" />
        </motion.div>

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 lg:px-10"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="kicker text-gold"
          >
            Premium Windows &amp; Doors · {CONTACT.area}
          </motion.p>

          <h1 className="mt-6 font-serif text-[3.25rem] leading-[0.98] text-cream sm:text-7xl lg:text-[6.5rem]">
            <MaskReveal lines={["Upgrade Your Home"]} delay={0.35} />
            <span className="italic text-gold">
              <MaskReveal lines={["with Elegance"]} delay={0.55} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-cream/80 md:text-lg"
          >
            Premium windows and doors designed for comfort, style and security —
            expertly installed and built to last.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <QuoteButton size="lg" testId="hero-request-quote-btn" />
            <Link
              to="/windows"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-cream/40 px-8 py-4 text-sm font-medium text-cream transition-all hover:border-gold hover:text-gold"
            >
              Explore Our Range
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 text-cream/60 lg:flex">
          <span className="kicker">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <Marquee
        items={[
          "Composite Doors",
          "French Doors",
          "All Windows",
          "CERTASS Certified Installers",
          "Energy Efficient",
          "10 Year Insurance-Backed Guarantee",
          "Expert Installation",
          "Made in Britain",
          "uPVC Doors",
        ]}
      />

      {/* ============ SAVINGS BAND ============ */}
      <section className="relative overflow-hidden bg-charcoal py-20 text-cream grain md:py-28" data-testid="savings">
        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="kicker text-gold">Limited time offer</p>
          </Reveal>
          <div className="mt-10 grid gap-y-14 md:grid-cols-2 md:gap-x-16">
            {[
              { amount: "£200", unit: "per window" },
              { amount: "£300", unit: "per door" },
            ].map((s, i) => (
              <Reveal key={s.unit} delay={i * 0.1}>
                <div className="border-l-2 border-gold pl-6">
                  <p className="text-sm uppercase tracking-[0.2em] text-cream/60">
                    Save up to
                  </p>
                  <p className="mt-2 font-serif text-7xl text-gold md:text-8xl">
                    {s.amount}
                  </p>
                  <p className="mt-1 text-lg text-cream/80">
                    {s.unit} through our recycling scheme
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 lg:grid-cols-4">
              {BENEFITS.map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40">
                    <Icon className="h-5 w-5 text-gold" />
                  </span>
                  <span className="text-sm font-medium text-cream/90">{label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FEATURED CATEGORIES ============ */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 md:py-32" data-testid="categories">
        <Reveal className="max-w-2xl">
          <p className="kicker text-gold-deep">Our range</p>
          <h2 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-6xl">
            Beautifully designed, built to perform.
          </h2>
          <p className="mt-5 text-stone">
            Two premium collections, each crafted to enhance your home inside and
            out — with a finish tailored to your style.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {[
            {
              title: "Windows",
              copy: "Bay and standard windows combining timeless design with outstanding energy efficiency, security and comfort.",
              img: IMAGES.windowBay,
              to: "/windows",
              test: "category-windows",
            },
            {
              title: "Doors",
              copy: "Composite, uPVC and French doors — stylish, secure and built to last, with a wide choice of colours and glass.",
              img: IMAGES.compositeMain,
              to: "/doors",
              test: "category-doors",
            },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <Link
                to={c.to}
                data-testid={c.test}
                className="group relative block overflow-hidden rounded-3xl"
              >
                <div className="aspect-[4/5] w-full overflow-hidden md:aspect-[5/6]">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="font-serif text-4xl text-cream">{c.title}</h3>
                      <p className="mt-3 max-w-sm text-sm leading-relaxed text-cream/80">
                        {c.copy}
                      </p>
                    </div>
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gold text-charcoal transition-transform duration-300 group-hover:rotate-45">
                      <ArrowUpRight className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ RECYCLING + GUARANTEE ============ */}
      <section className="bg-cream-deep py-24 md:py-32" data-testid="assurance">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="flex h-full flex-col justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
                <Recycle className="h-7 w-7 text-gold-deep" />
              </span>
              <h3 className="mt-6 font-serif text-3xl text-ink md:text-4xl">
                Better for your home, better for the planet
              </h3>
              <p className="mt-4 max-w-md leading-relaxed text-stone">
                Our windows are 100% recyclable and designed with energy
                efficiency in mind — helping reduce your carbon footprint. Through
                our recycling scheme you save up to{" "}
                <span className="font-semibold text-ink">£200 per window</span>{" "}
                and <span className="font-semibold text-ink">£300 per door</span>.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative overflow-hidden rounded-3xl bg-charcoal p-10 text-cream md:p-12">
              <div className="grain absolute inset-0 opacity-100" />
              <div className="relative z-10">
                <div className="flex items-center gap-4">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gold text-gold">
                    <ShieldPlus className="h-8 w-8" />
                  </span>
                  <div>
                    <p className="font-sans text-6xl font-semibold leading-none text-gold">10</p>
                    <p className="text-sm tracking-[0.15em] text-cream/70">
                      YEAR INSURANCE-BACKED GUARANTEE
                    </p>
                  </div>
                </div>
                <p className="mt-6 leading-relaxed text-cream/75">
                  Complete peace of mind with every installation. We use premium
                  materials and proven techniques for long-lasting performance —
                  and stand behind every project we complete.
                </p>
                <div className="mt-8">
                  <QuoteButton variant="gold" testId="assurance-request-quote-btn" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ MANIFESTO ============ */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 md:py-32" data-testid="manifesto">
        <Reveal className="max-w-2xl">
          <p className="kicker text-gold-deep">Crafted for quality · Installed with care</p>
          <h2 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-6xl">
            Why homeowners choose Eminence.
          </h2>
        </Reveal>

        <div className="mt-16 divide-y divide-gold/20 border-t border-gold/20">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="group grid gap-4 py-8 md:grid-cols-[auto_1fr_2fr] md:items-baseline md:gap-10">
                <span className="font-serif text-3xl text-gold md:text-4xl">{m.n}</span>
                <h3 className="font-serif text-2xl text-ink transition-colors group-hover:text-gold-deep md:text-3xl">
                  {m.title}
                </h3>
                <p className="max-w-xl leading-relaxed text-stone">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ LOCAL / TRANSFORM ============ */}
      <section className="relative overflow-hidden" data-testid="local">
        <div className="grid lg:grid-cols-2">
          <div className="order-2 flex items-center bg-charcoal px-5 py-20 text-cream lg:order-1 lg:px-16">
            <Reveal>
              <p className="kicker text-gold">Local &amp; reliable</p>
              <h2 className="mt-4 font-serif text-4xl leading-tight md:text-5xl">
                Proudly serving homes across Greater Manchester.
              </h2>
              <p className="mt-5 max-w-md leading-relaxed text-cream/75">
                From your first enquiry to the final installation and beyond, our
                team is here to ensure a smooth, stress-free experience with
                honesty and integrity. More light. More comfort. More value.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <QuoteButton variant="gold" testId="local-request-quote-btn" />
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3.5 text-sm font-medium text-cream transition-all hover:border-gold hover:text-gold"
                >
                  About Us
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="order-1 h-72 overflow-hidden lg:order-2 lg:h-auto">
            <img
              src={IMAGES.bifoldInterior}
              alt="Bright modern interior with premium glazing"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>
    </>
  );
}
